/*
 * ExerciseSelector.tsx
 *
 * New component: Allows users to search and select multiple exercises coming from the Supabase `exercises` table.
 * Selected exercises are returned to parent via callback.
 */
import { Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface Exercise {
  id: number;
  name: string;
  link: string | null;
}

interface ExerciseSelectorProps {
  selected: Exercise[];
  onChange: (list: Exercise[]) => void;
}

export function ExerciseSelector({ selected, onChange }: ExerciseSelectorProps) {
  const [allExercises, setAllExercises] = useState<Exercise[]>([]);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchExercises = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('exercises')
          .select('id, name, link')
          .order('name', { ascending: true });

        if (error) throw error;
        setAllExercises(data as Exercise[]);
      } catch (error) {
        /* eslint-disable no-console */
        console.error('[ExerciseSelector] Failed to fetch exercises:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchExercises();
  }, []);

  const filtered = query
    ? allExercises.filter((ex) => ex.name.toLowerCase().includes(query.toLowerCase()))
    : allExercises;

  const toggleExercise = (exercise: Exercise) => {
    const exists = selected.find((e) => e.id === exercise.id);
    if (exists) {
      onChange(selected.filter((e) => e.id !== exercise.id));
    } else {
      onChange([...selected, exercise]);
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative w-full">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search exercises..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="block w-full pl-9 pr-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 sm:text-sm"
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading exercises...</p>
      ) : (
        <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-200 dark:divide-gray-700">
          {filtered.map((exercise) => {
            const isSelected = selected.some((e) => e.id === exercise.id);
            return (
              <button
                key={exercise.id}
                type="button"
                onClick={() => toggleExercise(exercise)}
                className={`w-full flex items-center justify-between px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                  isSelected ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                }`}
              >
                <span className="text-gray-900 dark:text-gray-100">{exercise.name}</span>
                {isSelected && <X className="h-4 w-4 text-red-500" />}
              </button>
            );
          })}
        </div>
      )}

      {selected.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">Selected Exercises</h4>
          <div className="flex flex-wrap gap-2">
            {selected.map((ex) => (
              <button
                key={`sel-${ex.id}`}
                type="button"
                onClick={() => toggleExercise(ex)}
                className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800"
              >
                {ex.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
} 